
import { PURITY_MULTIPLIERS } from './constants';
import { MetalPrice } from './metal-price';

export type GoldPurity = keyof (typeof PURITY_MULTIPLIERS.ORO);
export type SilverPurity = keyof (typeof PURITY_MULTIPLIERS.ARGENTO);
export type Purity = GoldPurity | SilverPurity;
export type Metal = 'ORO' | 'ARGENTO';
export type Unit = 'g' | 'oz' | 'kg';

export interface ValuationResult {
    metal: Metal;
    purity: Purity;
    weight: number;
    unit: Unit;
    marketPrice: number;
    totalValue: number;
    purifiedPrice: number;
    formattedPrice: string;
}

export function getPurityMultiplier(metal: 'ORO' | 'ARGENTO', purity: Purity): number {
    const multipliers = PURITY_MULTIPLIERS[metal] as Record<string, number>;
    return multipliers[purity as string] || 1;
}

export const calculatePrice = (
    basePrice: number,
    purityMultiplier: number,
    unit: Unit = 'g',
    weight = 1
) => {
    let price = basePrice * purityMultiplier * weight;
    if (unit === 'oz') {
        price *= 31.1035; // grams per troy ounce
    }
    if (unit === 'kg') {
      price *= 1000;
    }
    return price;
};

export function calculateGoldPrice(rawPrice: MetalPrice, purity: GoldPurity): number {
    if (!rawPrice) return 0;
    const multiplier = getPurityMultiplier('ORO', purity);
    return calculatePrice(rawPrice.price, multiplier);
}
  
export function calculateSilverPrice(rawPrice: MetalPrice, purity: SilverPurity, unit: Unit): number {
    if (!rawPrice) return 0;
    const multiplier = getPurityMultiplier('ARGENTO', purity);
    return calculatePrice(rawPrice.price, multiplier, unit);
}

export function formatPrice(price: number, unit: Unit | 'g'): string {
    return `€${price.toFixed(2).replace('.', ',')}/${unit}`;
}

export function calculateDealerPrice(marketPrice: number, spread?: number | null, spreadType?: 'PERCENTAGE' | 'FIXED'): number {
  if (marketPrice <= 0 || typeof spread !== 'number') {
    return marketPrice;
  }
  
  // Per spread fissi, sottrae il valore dal prezzo di mercato
  if (spreadType === 'FIXED') {
    return Math.max(0, marketPrice - spread);
  }
  
  // Per spread percentuali, applica la percentuale
  // Negativi = sconto (es: -1.5% = dealer paga 1.5% meno del mercato)
  // Positivi = maggiorazione (es: +2% = dealer paga 2% più del mercato)
  return marketPrice * (1 + spread / 100);
}

export function calculateMetalValue(
    basePrice: number,
    metal: Metal,
    purity: Purity,
    weight: number,
    unit: Unit
): ValuationResult {
    const purityMultiplier = getPurityMultiplier(metal, purity);
    const purifiedPrice = basePrice * purityMultiplier;
    const totalValue = calculatePrice(basePrice, purityMultiplier, unit, weight);

    return {
        metal,
        purity,
        weight,
        unit,
        marketPrice: basePrice,
        totalValue,
        purifiedPrice,
        formattedPrice: `€${totalValue.toFixed(2).replace('.', ',')}`
    };
}
